import React, { useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ButtonSecondaryDark, IconClose, IconThumbsUp } from '@americanexpress/dls-react';

import WideView from 'src/components/atomic/layouts/wideView/WideView';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';
import { useReviewTransactions } from 'src/hooks/queries/transactions';
import LiteralsService from 'src/services/literals/LiteralsService';
import TransactionTable from 'src/components/atomic/organisms/transaction/TransactionsTable';
import colors from 'src/styles/colors.module.scss';
import styles from './transactionsReviewView.module.scss';
import { goToTransactions } from 'src/services/router.service';
import { UserUtils } from 'src/types/user.model';
import { useUser } from 'src/hooks/queries/users';
import { useDocumentTitleRaw } from 'src/hooks/useDocumentTitle';
import { useSearchParams } from 'src/hooks/useSearchParams';
import { ButtonType } from 'src/types/button-type.enum';
import IconButton from 'src/components/icon-button/IconButton';
import Card from 'src/components/atomic/atoms/card/Card';
import { trackClick, useTrackPage } from 'src/hooks/useAdobeTags';
import adobeTags from 'src/types/adobe-tags';
import Selectors from 'src/utils/selectors';
import { usePaginatedReviewableTransactions } from 'src/hooks/usePaginatedTransactions';
import TransactionsSearchInput from 'src/components/atomic/atoms/transactionsSearchInput/TransactionsSearchInput';
import { useSmoothScrollTo } from 'src/hooks/useSmoothScrollTo';
import { Formik } from 'formik';
import { TransactionModel } from 'src/types/transaction.model';
import * as Yup from 'yup';
import { Categories } from 'src/services/category.service';
import { SelectedTransactionsContext, useTransactionRows } from 'src/hooks/useSelectedTransactions';
import TransactionTableHeaderCheckbox from 'src/components/atomic/atoms/transactionsTableHeaderCheckbox/TransactionsTableHeaderCheckbox';

export interface TransactionReviewForm {
    selectedTransactions: TransactionModel[];
}

export default function TransactionsReviewView(): JSX.Element {
    useDocumentTitleRaw(LiteralsService.getLiteral('Budget.titles.reviewTransactions'));
    useTrackPage(LiteralsService.getLiteral('Budget.titles.reviewTransactions'));
    const history = useHistory();
    const { isLoading: isLoadingUser, data: user } = useUser();
    const { mutateAsync: reviewTransactions } = useReviewTransactions();
    const { isLoading: isLoadingPaginatedTransactions, data: paginatedTransactions } =
        usePaginatedReviewableTransactions();
    const reviewableTransactions = paginatedTransactions?.transactions;
    const isLoading: boolean = isLoadingPaginatedTransactions || isLoadingUser;
    useSmoothScrollTo(isLoading);

    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState<string>(searchParams.get('search'));
    const { transactionRows, setTransactionRows, selectedTransactions, setAllTransactionsSelect, showBanner } =
        useTransactionRows(reviewableTransactions);

    const isFullPlanUser: boolean = !UserUtils.isBudgetingUser(user);
    const totalTransactions = paginatedTransactions?.totalLength || 0;

    /** Handle search input change */
    const handleSearch = useCallback(
        (value: string): void => {
            trackClick(history.location.pathname, value === '' ? adobeTags.Search.Clear : adobeTags.Search.Apply);
            if (value) {
                searchParams.set('search', value);
            } else {
                searchParams.delete('search');
                setSearch('');
            }
            searchParams.set('page', '1');
            setSearchParams(searchParams);
        },
        [location.pathname, searchParams]
    );

    /** Approve all selected transactions */
    const bulkApprove = useCallback(async (): Promise<void> => {
        if (selectedTransactions && selectedTransactions.length === 0) return;
        const selectedTransactionIds = selectedTransactions.map((transaction) => transaction.id);
        await reviewTransactions(selectedTransactionIds);
        setAllTransactionsSelect(false);
        trackClick(history.location.pathname, adobeTags.Transactions.Bulk.Approve);
    }, [selectedTransactions]);

    /** Close bulk banner */
    const bulkClose = useCallback((): void => {
        setAllTransactionsSelect(false);
    }, []);

    const searchReset = useCallback(() => handleSearch(''), [handleSearch]);

    // Double loader causes janky behavior
    if (isLoading) return <div />;

    return (
        <WideView
            withDocTitle={false}
            hasBackgroundBanner
            showFooter
            data-cy={Selectors.Transactions.REVIEW_VIEW}
            title={LiteralsService.getLiteral('TransactionsView.review.reviewTransactions')}
            handleBack={(): void => {
                trackClick(history.location.pathname, adobeTags.Back);
                goToTransactions(isFullPlanUser, history);
            }}
            subtitle={LiteralsService.getLiteral('TransactionsView.review.transactionsThatAreSpendingOutliers')}
            hideBackButton
        >
            <Card showBackgroundBanner>
                <div className={styles.topRow}>
                    <Typography
                        variant={TypographyVariants.HEADING_L}
                        className={colors.brandStrong}
                        aria-live="polite"
                        data-cy={Selectors.Transactions.NO_OF_TRANSACTIONS}
                    >
                        {LiteralsService.getLiteral('TransactionsView.review.transactionsNeedReview', [
                            totalTransactions
                        ])}
                    </Typography>
                    <TransactionsSearchInput value={search} setSearch={setSearch} handleSearch={handleSearch} />
                </div>
                <TransactionTableHeaderCheckbox
                    noOfSelectedTransactions={selectedTransactions.length}
                    noOfFilteredTransactions={reviewableTransactions.length}
                    setAllTransactionsSelect={setAllTransactionsSelect}
                />
                <Formik
                    initialValues={{
                        selectedTransactions
                    }}
                    enableReinitialize
                    validationSchema={Yup.object().shape({
                        selectedTransactions: Yup.array().of(
                            Yup.object().shape({
                                id: Yup.number().required(),
                                groupUid: Yup.string().test('needsCategory', (groupUid, context) => {
                                    const currentTransactionId = context.parent.id;
                                    if (groupUid === Categories.UncategorizedGroupId) {
                                        return context.createError({
                                            path: context.parent.path,
                                            message: `Category is required for transaction ${currentTransactionId}`
                                        });
                                    }
                                    return true;
                                })
                            })
                        )
                    })}
                    onSubmit={bulkApprove}
                >
                    {({ handleSubmit }) => (
                        <>
                            {showBanner && selectedTransactions?.length > 0 && (
                                <div className={styles.bulkBanner} data-cy={Selectors.Transactions.BULK_APPROVE_BANNER}>
                                    <Typography
                                        variant={TypographyVariants.SUBTEXT_BOLD}
                                        className={colors.textLight}
                                        aria-live="polite"
                                    >
                                        {LiteralsService.getLiteral('TransactionsView.review.transactionsSelected', [
                                            selectedTransactions.length
                                        ])}
                                    </Typography>
                                    <ButtonSecondaryDark
                                        size="sm"
                                        type="submit"
                                        onClick={handleSubmit}
                                        data-cy={Selectors.Transactions.BULK_APPROVE_BUTTON}
                                        icon={<IconThumbsUp />}
                                    >
                                        {LiteralsService.getLiteral('TransactionsView.review.approve')}
                                    </ButtonSecondaryDark>
                                    <IconButton
                                        variant={ButtonType.TERTIARY_DARK}
                                        icon={<IconClose />}
                                        data-cy="bulkReviewCloseSelect"
                                        title={LiteralsService.getLiteral('TransactionsView.review.closeBulkEdit')}
                                        className={styles.closeIcon}
                                        onClick={bulkClose}
                                    />
                                </div>
                            )}
                            <SelectedTransactionsContext.Provider
                                value={{
                                    setAllTransactionsSelect
                                }}
                            >
                                <TransactionTable
                                    transactionRows={transactionRows}
                                    setTransactionRows={setTransactionRows}
                                    searchReset={searchReset}
                                    isReview
                                    totalPages={Math.ceil(
                                        paginatedTransactions.totalLength / paginatedTransactions.pageSize
                                    )}
                                />
                            </SelectedTransactionsContext.Provider>
                        </>
                    )}
                </Formik>
            </Card>
        </WideView>
    );
}
